import type { FieldTemplateProps, RJSFSchema } from '@rjsf/utils';
import type { ChangeEvent, FocusEvent } from 'react';
import { useCallback } from 'react';
import { CURRENCY_OPTIONS } from '../../../utils/currencies';
import { Input, InputVariant } from '../../input';

interface CurrencyWidgetProps extends Omit<FieldTemplateProps, 'onChange'> {
  onChange: (
    value: number | undefined | { currency?: string; amount?: number }
  ) => void;
}

/**
 * Technically a field with `amount` and `currency`
 * properties, but we treat it like a widget
 */
interface CurrencySchemaProperties {
  currency?: {
    default?: string;
    readOnly?: boolean;
  };
  amount?: {
    minimum?: number;
  };
  emptyReadOnly?: boolean;
}

interface CurrencyFormContext {
  defaultCurrency?: string;
}

const CurrencyWidget = ({
  id,
  label,
  schema,
  formData,
  registry,
  disabled,
  onChange,
  rawErrors,
  hideError,
  uiSchema,
}: CurrencyWidgetProps) => {
  const formContext = registry?.formContext as unknown as
    | CurrencyFormContext
    | undefined;
  const readOnly = schema.readOnly;
  const schemaProps = schema.properties as unknown as CurrencySchemaProperties;
  const emptyReadOnly = !!schemaProps.emptyReadOnly;
  const currencySchema = schemaProps.currency;
  const defaultCurrency =
    currencySchema?.default ?? formContext?.defaultCurrency;
  const fixedCurrency = currencySchema?.readOnly ? defaultCurrency : undefined;
  const amountSchema = schemaProps.amount;
  const amountMinimum = amountSchema?.minimum;

  const formDataObj =
    (formData as { currency?: string; amount?: number } | undefined) ?? {};

  const onChangeAmount = useCallback(
    (event: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => {
      if (event.type === 'blur') {
        // Ignore blur events coming from onChange
        return;
      }
      const amount = event.target.value
        ? Number.parseFloat(event.target.value)
        : undefined;
      if (schema.type === 'number') {
        onChange(amount);
        return;
      }
      if (!formDataObj?.currency && fixedCurrency) {
        onChange({
          ...formDataObj,
          currency: fixedCurrency,
          amount: amount,
        });
        return;
      }
      onChange({
        ...formDataObj,
        amount: amount,
      });
    },
    [fixedCurrency, formDataObj, onChange, schema.type]
  );

  const onChangeCurrency = useCallback(
    (currency: string) => {
      onChange({ ...formDataObj, currency });
    },
    [formDataObj, onChange]
  );

  const amountValue =
    emptyReadOnly && readOnly ? '' : (formDataObj?.amount ?? '');

  return (
    <Input
      id={id}
      name={label}
      width="100%"
      value={amountValue ?? ''}
      variant={InputVariant.Amount}
      disabled={disabled || readOnly}
      hasError={rawErrors && rawErrors.length > 0 && !hideError && !readOnly}
      errorMessage={readOnly ? undefined : rawErrors?.join(', ')}
      onChange={onChangeAmount}
      onCurrencyChange={onChangeCurrency}
      currencyList={fixedCurrency ? undefined : CURRENCY_OPTIONS}
      currency={fixedCurrency}
      defaultCurrency={defaultCurrency}
      min={amountMinimum}
      hintText={schema.description}
      infoMessage={
        typeof uiSchema?.infoMessage === 'function'
          ? uiSchema.infoMessage(String(formDataObj.amount ?? ''))
          : typeof uiSchema?.infoMessage === 'string'
            ? uiSchema.infoMessage
            : undefined
      }
      warningMessage={
        typeof uiSchema?.warningMessage === 'function'
          ? uiSchema.warningMessage(String(formDataObj.amount ?? ''))
          : typeof uiSchema?.warningMessage === 'string'
            ? uiSchema.warningMessage
            : undefined
      }
    />
  );
};

export default CurrencyWidget;
